<template>
	<div class="page">
		<header-block title="Saved to Spend"></header-block>
		<CustomerLoadWait :customer="customer && statement" :cardBlocks="true" :withTopCard="true" />

		<div v-if="customer && statement" class="content">
			<!-- Balance Card Section -->
			<div class="balance-card">
				<div class="balance-top">
						<p class="balance-title">Your Saved to Spend pot</p>
						<div class="balance-amount-row">
							<img :src="require('@/assets/icons/coins-swap.png')" alt="coins" class="balance-icon" />
							<span class="balance-amount">{{ formattedBalance }}</span>
						</div>
						<div class="balance-divider"></div>
						<p class="balance-description">The cashback you have earned through buying vouchers will show here. You earn rewards when spending from your pot too!</p>
						<button class="shop-button" @click="navigateToShop">
							<span>Shop</span>
							<img :src="require('@/assets/arrow-right.png')" alt="arrow" class="arrow-icon" />
						</button>
					</div>

					<!-- Stats Row -->
				<div class="stats-row">
					<SavedToSpendStatCard
						:icon="require('@/assets/icons/credit-card-plus.png')"
						label="Saved in the last year"
						:value="formattedSavedLastYear"
						variant="gold"
					/>
					<SavedToSpendStatCard
						:icon="require('@/assets/icons/shopping-bag.png')"
						label="All time savings"
						:value="formattedAllTimeSavings"
						variant="blue"
					/>
					<SavedToSpendStatCard
						:icon="require('@/assets/icons/coins-stacked.png')"
						label="Average reward"
						:value="formattedAverageReward"
						variant="purple"
					/>
				</div>
			</div>

			<!-- Latest Activity -->
			<DiscountTransactionsSection
				v-if="statementDays && statementDays.length > 0"
				:days="statementDays"
				:formatter="formatter"
				:maxDays="3"
				:maxVouchers="3"
				@view-more="navigateToFullStatement"
			/>

			<!-- Hero Carousel Section -->
			<div v-if="heroWalkthroughData && heroCarouselReady" class="hero-carousel-section">
				<MGWalkthrough
					:walkthroughData="heroWalkthroughData"
					variant="sheet"
					:compact="true"
					:imageHeight="'220'"
					:height="determineCarouselHeight()"
					@slide-index-changed="handleHeroSlideChange"
				/>
			</div>

			<!-- FAQ Section -->
			<FaqsSection v-if="faqs.length" class="faq-section" :faqs="faqs" :limit="5" faqName="save-to-spend" />
		</div>

		<DrawerPanel
			ref="transactionsDrawer"
			bottom="-18px"
			openHeight="95%"
			closedHeight="0px"
			title="All Activity"
			backgroundColor="white"
			openBackgroundColor="white"
		>
			<DiscountTransactionsSection
				v-if="statementDays && statementDays.length > 0"
				:days="statementDays"
				:formatter="formatter"
				title=""
			/>
		</DrawerPanel>
	</div>
</template>

<script>
import Services from '../mixins/Services';
import Helpers from '../mixins/Helpers';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import SavedToSpendStatCard from './elements/SavedToSpendStatCard.vue';
import DiscountTransactionsSection from './elements/DiscountTransactionsSection.vue';
import MGWalkthrough from './elements/MGWalkthrough.vue';
import FaqsSection from './FaqsSection.vue';
import DrawerPanel from './elements/DrawerPanel.vue';

export default {
	mixins: [Services, Helpers],
	components: { CustomerLoadWait, HeaderBlock, SavedToSpendStatCard, DiscountTransactionsSection, MGWalkthrough, FaqsSection, DrawerPanel },
	data() {
		return {
			statementDays: [],
			statement: null,
			heroWalkthroughData: null,
			heroCarouselReady: false,
			faqs: []
		}
	},
	computed: {
		formattedBalance() {
			return this.formatter.format(this.statement?.savedToSpendBalance || 0);
		},
		formattedSavedLastYear() {
			const value = this.statement?.rolling12MonthsSavings;
			return value ? this.formatter.format(value) : '£--';
		},
		formattedAllTimeSavings() {
			const value = this.statement?.allTimeSavings;
			return value ? this.formatter.format(value) : '£--';
		},
		formattedAverageReward() {
			const pct = this.statement?.averageSavingsPercentage || 0;
			return pct.toFixed(1) + '%';
		}
	},
	methods: {
		onLoad: async function() {
			this._getCustomer(() => {
				this._getDiscountsStatement((result) => {
					this.statement = result;
					this.statementDays = result?.days || [];
				});
			});
			this._getWalkthrough('saved-to-spend-hero', (result) => {
				this.heroWalkthroughData = result;
			});
			this._getFaqs('save-to-spend', (result) => {
				this.faqs = result?.faqItems || [];
			});
		},
		navigateToShop() {
			this.$router.push('/shopvouchers');
		},
		navigateToFullStatement() {
			this.$nextTick(() => {
				if (this.$refs.transactionsDrawer) {
					this.$refs.transactionsDrawer.openDrawer();
				}
			});
		},
		handleHeroSlideChange() {
			// Handle slide change if needed
		},
		determineCarouselHeight() {
			if (window.innerHeight < 700) {
				return '150px';
			} else {
				return '240px';
			}
		}
	},
	mounted: function () {
		this.onLoad();
		setTimeout(() => {
			this.heroCarouselReady = true;
		}, 50);
	}
}
</script>

<style scoped>
.page {
	display: flex;
	flex-direction: column;
	min-height: 100vh;
	padding-bottom: 20px;
}

.content {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.balance-card {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.balance-top {
	border-radius: 8px;
	padding: 24px 32px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
}

.balance-title {
	font-size: 13px;
	font-weight: bold;
	color: #413655;
	margin: 0;
	text-align: center;
}

.balance-amount-row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.balance-icon {
	width: 24px;
	height: 24px;
}

.balance-amount {
	font-size: 24px;
	font-weight: bold;
	color: #413655;
}

.balance-divider {
	width: 100%;
	height: 1px;
	background: #DCDCDC;
}

.balance-description {
	font-size: 11px;
	color: #413655;
	margin: 0;
	text-align: center;
	line-height: 1.1;
}

.shop-button {
	display: flex;
	align-items: center;
	gap: 4px;
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 0;
}

.shop-button span {
	font-size: 13px;
	font-weight: bold;
	color: #413655;
}

.arrow-icon {
	width: 14px;
	height: 10px;
}

.stats-row {
	display: flex;
	gap: 12px;
	align-items: stretch;
}

.hero-carousel-section {
	margin-bottom: -30px;
	margin-top: -20px;
}

.faq-section {
	border-radius: 8px;
	background-color: white;
}
</style>
